import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext.tsx';
import { api } from '../api/client.ts';
import { setFacilityOverride } from '../api/client.ts';
import { getImageSrc } from '../utils/imageUrl.ts';

interface FacilityContextValue {
  facilityId: number;
  facilityName: string;
  setFacility: (id: number, name: string) => void;
}

const FacilityContext = createContext<FacilityContextValue>({
  facilityId: 0,
  facilityName: '',
  setFacility: () => {},
});

function readSavedFacility(): { id: number; name: string } | null {
  const savedId = localStorage.getItem('selectedFacilityId');
  const savedName = localStorage.getItem('selectedFacilityName');
  if (!savedId || !savedName) return null;
  const id = parseInt(savedId, 10);
  return isNaN(id) ? null : { id, name: savedName };
}

function clearSavedFacility() {
  localStorage.removeItem('selectedFacilityId');
  localStorage.removeItem('selectedFacilityName');
}

export function FacilityProvider({ children }: { children: ReactNode }) {
  const { user, refreshUser } = useAuth();
  const qc = useQueryClient();
  // Track whether we've ever seen a logged-in user so we don't mistake
  // "initial load with null user" for a logout event.
  const hadUserRef = useRef(false);

  // Initialize from localStorage immediately so the API override header is set
  // before the first data fetch (avoids one extra round-trip with wrong facility).
  const [override, setOverride] = useState<{ id: number; name: string } | null>(() => {
    const saved = readSavedFacility();
    if (saved) {
      setFacilityOverride(saved.id); // set header synchronously on page load
      return saved;
    }
    return null;
  });

  // When user identity changes: validate the saved facility against the user's role.
  useEffect(() => {
    if (user) {
      hadUserRef.current = true;
      const isAdmin = user.role === 'admin' || user.role === 'org_admin';
      if (!isAdmin) {
        // Non-admins cannot switch facilities — clear any stale saved state
        setOverride(null);
        setFacilityOverride(null);
        clearSavedFacility();
      }
      // Admins keep the restored override — nothing else to do
    } else if (hadUserRef.current) {
      // User explicitly logged out (was previously logged in) — clear everything.
      // Do NOT run this branch on initial app load where user is null before
      // refreshUser() resolves — that would wipe the localStorage-restored override.
      hadUserRef.current = false;
      setOverride(null);
      setFacilityOverride(null);
      clearSavedFacility();
    }
  }, [user?.id]);

  const facilityId = override?.id ?? user?.facilityId ?? 0;
  const facilityName = override?.name ?? user?.facilityName ?? '';

  function setFacility(id: number, name: string) {
    setOverride({ id, name });
    localStorage.setItem('selectedFacilityId', String(id));
    localStorage.setItem('selectedFacilityName', name);
    // Only send override header when it differs from the user's own facility
    const isOverride = user ? id !== user.facilityId : false;
    setFacilityOverride(isOverride ? id : null);
    void qc.invalidateQueries();
    // Refetch /auth/me so user.locked/bypassExpiresAt (v143) reflect the NEWLY active facility --
    // those are computed server-side off req.user.facilityId (which the X-Facility-ID header set
    // above changes for future requests), so without this an org_admin/admin switching from a
    // locked facility to an unlocked one (or vice versa) would keep App.tsx's LockGate showing the
    // stale state until something else happened to trigger a refetch.
    void refreshUser();
  }

  return (
    <FacilityContext.Provider value={{ facilityId, facilityName, setFacility }}>
      {children}
    </FacilityContext.Provider>
  );
}

export const useFacility = () => useContext(FacilityContext);

// Resolved facility logo URL for print/export output — same fallback Settings.tsx's
// FacilityFormCard already uses (logo_url, or /uploads/<logo_path> for file uploads without a
// standalone URL). Reused here rather than re-fetching/re-deriving this per print component.
export function useFacilityLogoUrl(): string | null {
  const { facilityId } = useFacility();
  const { data } = useQuery({
    queryKey: ['facility-logo', facilityId],
    queryFn: () => api.get<{ logo_url: string | null; logo_path: string | null }>(`/facilities/${facilityId}`),
    enabled: !!facilityId,
    staleTime: 5 * 60 * 1000,
  });
  const fac = data?.data;
  if (!fac) return null;
  return getImageSrc(fac.logo_url ?? fac.logo_path);
}
